
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy load all non-critical routes to improve TTI
const NotFound = lazy(() => import("./pages/NotFound"));
const Legal = lazy(() => import("./pages/Legal"));
const FAQs = lazy(() => import("./pages/FAQs"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const Brands = lazy(() => import("./pages/Brands"));
const BrandSignup = lazy(() => import("./pages/BrandSignup"));
const CreatorSignup = lazy(() => import("./pages/CreatorSignup"));
const CreatorConfirmation = lazy(() => import("./pages/CreatorConfirmation"));
const Leads = lazy(() => import("./pages/Leads"));
const Auth = lazy(() => import("./pages/Auth"));
const UrlShortener = lazy(() => import("./pages/UrlShortener"));
const RedirectHandler = lazy(() => import("./pages/RedirectHandler"));

// Lazy load non-critical components
const CookieBanner = lazy(() => import("./components/CookieBanner"));

// Optimize QueryClient for better TTI
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

// Minimal route loading fallback to prevent layout shift
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-8 w-8 animate-pulse rounded-full bg-muted"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Toaster />
    <Sonner />
    <Suspense fallback={null}>
      <CookieBanner />
    </Suspense>
    <BrowserRouter>
      <AnalyticsProvider>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/brands" element={
            <Suspense fallback={<RouteLoader />}>
              <Brands />
            </Suspense>
          } />
          <Route path="/blog" element={
            <Suspense fallback={<RouteLoader />}>
              <Blog />
            </Suspense>
          } />
          <Route path="/blog/:slug" element={
            <Suspense fallback={<RouteLoader />}>
              <BlogPostPage />
            </Suspense>
          } />
          <Route path="/brandsignup" element={
            <Suspense fallback={<RouteLoader />}>
              <BrandSignup />
            </Suspense>
          } />
          <Route path="/creatorsignup" element={
            <Suspense fallback={<RouteLoader />}>
              <CreatorSignup />
            </Suspense>
          } />
          <Route path="/creatorconfirmation" element={
            <Suspense fallback={<RouteLoader />}>
              <CreatorConfirmation />
            </Suspense>
          } />
          <Route path="/auth" element={
            <Suspense fallback={<RouteLoader />}>
              <Auth />
            </Suspense>
          } />
          <Route path="/leads" element={
            <Suspense fallback={<RouteLoader />}>
              <Leads />
            </Suspense>
          } />
          <Route path="/url-shortener" element={
            <Suspense fallback={<RouteLoader />}>
              <UrlShortener />
            </Suspense>
          } />
          <Route path="/r/:shortCode" element={
            <Suspense fallback={<RouteLoader />}>
              <RedirectHandler />
            </Suspense>
          } />
          <Route path="/legal" element={
            <Suspense fallback={<RouteLoader />}>
              <Legal />
            </Suspense>
          } />
          <Route path="/faqs" element={
            <Suspense fallback={<RouteLoader />}>
              <FAQs />
            </Suspense>
          } />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={
            <Suspense fallback={<RouteLoader />}>
              <NotFound />
            </Suspense>
          } />
        </Routes>
      </AnalyticsProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
